import * as Sentry from "@sentry/browser";
import classNames from "classnames";
import {
  DYNAMIC_CONTENT_TYPES,
  MEDIA_ENTRY_APPLICATION,
  MEDIA_ENTRY_CONTENT_GROUP,
  MEDIA_ENTRY_USER_CONTENT,
  MEDIA_ENTRY_WEBPAGE,
  ROOT_URL as STRATEGY_V2_ROOT_URL,
} from "fleet/constant/media-library";
import StrategyUtils from "fleet/view/strategy/util";
import { captureStack } from "integrations/sentry";
import PropTypes from "prop-types";
import React from "react";
import { Button, ButtonToolbar } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getById, isStoreReady, makeGetAllAsArray } from "redux-store";
import * as App from "redux-store/applications/selectors";
import { isAdmin, isOperator } from "redux-store/users/selectors";
import { NavLoader } from "ui/loader";
import { Table, Column } from "ui/table";
import { devLogger } from "util/logging";
import { utcToLocal } from "util/time";
import Box from "view/layout/box";
import Page from "view/layout/page";
import { VIEW } from "view/views";

const getAllStrategiesAsArray = makeGetAllAsArray();

class StrategyV2ListPage extends React.PureComponent {
  static get propTypes() {
    return {
      applications: PropTypes.object.isRequired,
      archived: PropTypes.bool,
      archiveStrategy: PropTypes.func,
      contentGroups: PropTypes.object.isRequired,
      history: PropTypes.object.isRequired,
      strategies: PropTypes.object.isRequired,
      user: PropTypes.object.isRequired,
      userContents: PropTypes.object.isRequired,
      viewUrl: PropTypes.string.isRequired,
    };
  }

  static get defaultProps() {
    return {
      archived: false,
      archiveStrategy: () => {},
    };
  }

  constructor() {
    super();

    this._formatName = this._formatName.bind(this);
    this._formatOperations = this._formatOperations.bind(this);
    this._formatStrategy = this._formatStrategy.bind(this);
  }

  _archive(strategyId) {
    const { archiveStrategy } = this.props;

    const successMessage = this.props.archived
      ? "Strategy unarchived successfully."
      : "Strategy archived successfully.";

    archiveStrategy(strategyId, !this.props.archived, successMessage);
  }

  _clone(strategyId) {
    const redirectTo = `${STRATEGY_V2_ROOT_URL}/${strategyId}/${VIEW.CLONE}`;
    this.props.history.push(redirectTo);
  }

  _formatName(name, row) {
    const { viewUrl } = this.props;

    return <Link to={`${viewUrl}${row.id}/${VIEW.EDIT}`}>{name}</Link>;
  }

  _formatDate(date) {
    return utcToLocal(date);
  }

  _formatSpots(idx, priority, strategyId) {
    // Fallback is not required.
    if (idx === 1 && !priority) {
      return { rows: [] };
    }

    let canNotRun = false;
    let hasArchived = false;

    const priorityLevel = StrategyUtils.getPriorityLevel(idx);
    const rows = [<dt key={`p-${idx}-key`}>{priorityLevel.text}</dt>];

    const hasDynamicContent = priority.some((spot) =>
      DYNAMIC_CONTENT_TYPES.find((type) => type === spot.type),
    );

    priority.forEach((spot, spotIdx) => {
      const { appId, contentId, contentGroupId, type } = spot;

      let result;
      switch (type) {
        case MEDIA_ENTRY_APPLICATION:
          result = this._getByApp(appId);
          break;

        case MEDIA_ENTRY_CONTENT_GROUP:
          result = this._getByContentGroup(contentGroupId);
          break;

        case MEDIA_ENTRY_USER_CONTENT:
          result = this._getByContent(contentId);
          break;

        case MEDIA_ENTRY_WEBPAGE:
          result = this._getByWebpage(spot);
          break;

        default:
          result = {};
          this._invalidSpotTypeWarning(spot, strategyId, idx, spotIdx);
          break;
      }

      if (result.canNotRun) {
        canNotRun = true;
      }

      if (result.hasArchived) {
        hasArchived = true;
      }

      rows.push(
        <dd key={`p-${idx}-${spotIdx}-val`}>{result.itemValue || "N/A"}</dd>,
      );
    });

    return {
      canNotRun,
      hasArchived,
      hasDynamicContent,
      rows,
    };
  }

  _formatStrategy(strategy) {
    const [main, fallback] = strategy.priorities;

    const isLegacyStrategy = this._isLegacyStrategy(main, fallback);
    if (isLegacyStrategy) {
      return (
        <div>
          <div className="bg-warning strategy-list-warning">
            <i className="fa fa-warning" /> This strategy is a legacy format.
            Please archive it and create a new one.
          </div>
        </div>
      );
    }

    const mainLoop = this._formatSpots(0, main, strategy.id);
    const fbLoop = this._formatSpots(1, fallback, strategy.id);

    const canRun = !(mainLoop.canNotRun || fbLoop.canNotRun);
    const hasArchived = mainLoop.hasArchived || fbLoop.hasArchived;
    const spotEntries = [...mainLoop.rows, ...fbLoop.rows];
    const hasDynamicContent = fbLoop.hasDynamicContent;

    const classes = classNames({
      "bg-warning": true,
      "strategy-list-warning": true,
      hide: canRun,
    });

    const archivedClasses = classNames({
      "bg-warning": true,
      "strategy-list-warning": true,
      hide: !hasArchived,
    });

    const dynamicFbClasses = classNames({
      "bg-warning": true,
      "strategy-list-warning": true,
      hide: !hasDynamicContent,
    });

    return (
      <div>
        <dl className="dl-horizontal">{spotEntries}</dl>
        <div className={classes}>
          <i className="fa fa-warning" /> This strategy has missing applications
          and cannot be deployed.
        </div>
        <div className={archivedClasses}>
          <i className="fa fa-warning" /> This strategy contains archived
          content.
          <p>
            <small>
              The content will still play, but you cannot add it to other
              strategies.
            </small>
          </p>
        </div>
        <div className={dynamicFbClasses}>
          <i className="fa fa-warning" /> This strategy's fallback loop contains
          dynamic content.
          <p>
            <small>
              The content will still play, but you cannot deploy this strategy
              to other devices. Please ensure the fallback loop contains only
              static content such as images or videos.
            </small>
          </p>
        </div>
      </div>
    );
  }

  _formatOperations(unused, strategy) {
    const { archived, user } = this.props;

    if (!isAdmin(user) && !isOperator(user)) {
      return null;
    }

    let label = "Archive";
    let icon = "archive";

    if (archived) {
      label = "Unarchive";
      icon = "folder-open";
    }

    const archiveBtn = (
      <ButtonToolbar className="operation-btn">
        <Button onClick={() => this._archive(strategy.id)} size="sm" variant="secondary">
          <i className={`fa fa-${icon}`} /> {label}
        </Button>
      </ButtonToolbar>
    );
    const copyBtn = (
      <ButtonToolbar className="operation-btn">
        <Button onClick={() => this._clone(strategy.id)} size="sm" variant="secondary">
          <i className="fa fa-copy" /> Clone
        </Button>
      </ButtonToolbar>
    );

    if (isOperator(user)) {
      return copyBtn;
    }

    return (
      <div>
        {archiveBtn}
        {copyBtn}
      </div>
    );
  }

  _getByApp(appId) {
    const { applications } = this.props;
    const result = {
      canNotRun: false,
      itemValue: null,
    };

    const app = getById(applications, appId);
    if (!App.installed(app)) {
      result.canNotRun = true;
      return result;
    }

    result.itemValue = (
      <Link to={`/applications/${app.id}`}>{App.name(app)}</Link>
    );

    return result;
  }

  _getByContent(contentId) {
    const { userContents } = this.props;
    const result = {
      canNotRun: false,
      hasArchived: false,
      itemValue: null,
    };

    const content = getById(userContents, contentId);
    if (!content) {
      result.canNotRun = true;
      return result;
    }

    result.itemValue = <div>{content.name} (Content)</div>;
    if (content.archived) {
      result.hasArchived = true;
    }

    return result;
  }

  _getByContentGroup(contentGroupId) {
    const { userContents, contentGroups } = this.props;
    const result = {
      canNotRun: false,
      hasArchived: false,
      itemValue: null,
    };

    const contentGroup = getById(contentGroups, contentGroupId);
    if (!contentGroup) {
      result.canNotRun = true;
      return result;
    }

    result.itemValue = <div>{contentGroup.name} (Content Group)</div>;

    result.hasArchived = contentGroup.content.some((c) => {
      const content = getById(userContents, c.id) || {};
      return content.archived;
    });

    return result;
  }

  _getByWebpage(spot) {
    const result = {
      itemValue: null,
    };

    result.itemValue = (
      <a href={spot.url} rel="noreferrer noopener" target="_blank">
        {spot.name} (Web Page)
      </a>
    );

    return result;
  }

  _invalidSpotTypeWarning(spot, strategyId, idx, spotIdx) {
    const serialized = JSON.stringify(spot);
    const warning =
      "Unhandled strategy loop spot type " +
      `(${spot.type}). Strategy ` +
      `${strategyId}, priority ${idx}, spot ${spotIdx} - ${serialized}`;

    devLogger(console.warn, warning, spot);

    captureStack();
    Sentry.captureMessage(warning, Sentry.Severity.Warning);
  }

  _isLegacyStrategy(main, fallback = []) {
    // Missing media entry type, means it is a legacy strategy
    // and cannot be deployed.
    return [...main, ...fallback].some((s) => !s.type);
  }

  renderContent() {
    const { archived, applications, strategies } = this.props;
    const storesLoaded = isStoreReady(applications) && isStoreReady(strategies);

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    const data = getAllStrategiesAsArray(strategies)
      .filter((s) => s.archived === archived)
      // Inject strategy id into strategy for debugging errors.
      .map((s) => ({ ...s, strategy: { ...s.strategy, id: s.id } }));

    return (
      <Table
        data={data}
        defaultSorted={[{ dataField: "create_date", order: "desc" }]}
        hover={true}
        pagination={true}
        search={true}
      >
        {this._renderColumns()}
      </Table>
    );
  }

  _renderColumns() {
    const { user } = this.props;

    const columns = [
      <Column
        key="id"
        dataField="id"
        dataSort={true}
        width="300px"
        isKey={true}
        hidden={true}
      >
        ID
      </Column>,
      <Column
        key="name"
        dataField="name"
        dataSort={true}
        dataFormat={this._formatName}
      >
        Name
      </Column>,
      <Column
        key="strategy"
        dataField="strategy"
        dataSort={false}
        dataFormat={this._formatStrategy}
      >
        Strategy
      </Column>,
      <Column
        key="create_date"
        dataField="create_date"
        dataSort={true}
        dataFormat={this._formatDate}
        width="170px"
      >
        Created At
      </Column>,
    ];

    if (isAdmin(user) || isOperator(user)) {
      columns.push(
        <Column
          key="operations"
          dataField=""
          dataFormat={this._formatOperations}
          width="170px"
        >
          Operations
        </Column>,
      );
    }

    return columns;
  }

  _renderArchiveButton() {
    let button;
    if (this.props.archived) {
      button = (
        <Link className="btn btn-default" to="/v2/strategies">
          <i className="fa fa-folder-open" /> View Unarchived
        </Link>
      );
    } else {
      button = (
        <Link className="btn btn-default" to="/v2/strategies/archived">
          <i className="fa fa-archive" /> View Archived
        </Link>
      );
    }

    return <div className="page-tools">{button}</div>;
  }

  render() {
    const { archived } = this.props;
    const title = archived ? "Archived Strategies" : "Strategy List";

    return (
      <Page title={title} toolbox={this._renderArchiveButton()}>
        <Box title="" noHeader>
          {this.renderContent()}
        </Box>
      </Page>
    );
  }
}

export { StrategyV2ListPage };
